import React, { useCallback, useMemo, useState } from 'react';
import { SETTING } from 'interfaces/settings';
import { PROSPECT_PHONE_TYPE, PHONE_TYPE_TO_HUMAN_READABLE, Platform } from 'interfaces/db';
import { PhonePreference, PhoneKeys } from 'interfaces/settings';
import { useAutodialerSettings } from '../../hooks/useAutodialerSettings';
import { Typography } from 'interfaces/typography';
import { Phone, GripVertical } from 'lucide-react';
import { getPhoneLabel, getPhoneTypesForDialer } from 'components/AutodialerSettings/config/defaultValues';

const DEFAULT_POWER_RANKINGS = [PROSPECT_PHONE_TYPE.MOBILE, PROSPECT_PHONE_TYPE.VOIP, PROSPECT_PHONE_TYPE.WORK, PROSPECT_PHONE_TYPE.HOME, PROSPECT_PHONE_TYPE.OTHER];

export function PhonePreferenceControl() {
    const { getValueForSetting, updateSetting, selectedPlatform } = useAutodialerSettings();
    const currentValue = getValueForSetting(SETTING.PHONE_PREFERENCE) as Record<PROSPECT_PHONE_TYPE, PhonePreference> | null;
    
    
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const availablePhoneTypes = useMemo(() => {
        return selectedPlatform ? getPhoneTypesForDialer(selectedPlatform) : DEFAULT_POWER_RANKINGS;
    }, [selectedPlatform, getPhoneTypesForDialer]);

    const value = useMemo(() => {
        if (currentValue && selectedPlatform && !availablePhoneTypes.every(type => currentValue[type])) {
            return { ...currentValue, ...Object.fromEntries(availablePhoneTypes.map(type => [type, { [PhoneKeys.ACTIVE]: true, [PhoneKeys.DOUBLE_TAPS]: 1, [PhoneKeys.ORDER]: 0 }])) }
        }
        return currentValue;
    }, [currentValue, selectedPlatform, availablePhoneTypes]);
    // Get current preference map or create default
    
    const phonePreferenceMap: Record<PROSPECT_PHONE_TYPE, PhonePreference> = useMemo(() => {
        if (!value) {
            // Create default preferences
            const defaultMap = Object.fromEntries(availablePhoneTypes.map((phoneType, index) => [phoneType, {
                    [PhoneKeys.ACTIVE]: true,
                    [PhoneKeys.DOUBLE_TAPS]: 1,
                    [PhoneKeys.ORDER]: index
                }]));
            return defaultMap as Record<PROSPECT_PHONE_TYPE, PhonePreference>;
        }
        return value;
    }, [value, availablePhoneTypes]);

    const getValueForPhonePreference = useCallback((phoneType: PROSPECT_PHONE_TYPE, key: keyof PhonePreference) => {
        const preference = phonePreferenceMap[phoneType];
        return preference ? preference[key] : (key === PhoneKeys.ACTIVE ? true : key === PhoneKeys.DOUBLE_TAPS ? 1 : 0);
    }, [phonePreferenceMap]);

    const updatePhonePreference = useCallback((phoneType: PROSPECT_PHONE_TYPE, key: keyof PhonePreference, newValue: any) => {
        const newMap: Record<PROSPECT_PHONE_TYPE, PhonePreference> = { ...phonePreferenceMap };
        const current = newMap[phoneType] || {
            [PhoneKeys.ACTIVE]: true,
            [PhoneKeys.DOUBLE_TAPS]: 1,
            [PhoneKeys.ORDER]: 0
        };
        newMap[phoneType] = { ...current, [key]: newValue };
        updateSetting(SETTING.PHONE_PREFERENCE, newMap);
    }, [phonePreferenceMap, updateSetting]);

    // Sort phone types by order for display
    const sortedPhoneTypes = useMemo(() => {
        return [...availablePhoneTypes].sort((a, b) => {
            const orderA = getValueForPhonePreference(a, PhoneKeys.ORDER);
            const orderB = getValueForPhonePreference(b, PhoneKeys.ORDER);
            return (orderA as number) - (orderB as number);
        });
    }, [availablePhoneTypes, getValueForPhonePreference]);

    // Handle drag and drop reordering
    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', '');
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOverIndex(null);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        setDragOverIndex(null);
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        // Reorder all items
        const newOrder = [...sortedPhoneTypes];
        const draggedItem = newOrder[draggedIndex];
        
        // Remove dragged item and insert it at new position
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(dropIndex, 0, draggedItem);
        
        // Update order values for all items
        const newMap: Record<PROSPECT_PHONE_TYPE, PhonePreference> = { ...phonePreferenceMap };
        newOrder.forEach((phoneType, index) => {
            const current = newMap[phoneType] || {
                [PhoneKeys.ACTIVE]: true,
                [PhoneKeys.DOUBLE_TAPS]: 1,
                [PhoneKeys.ORDER]: index
            };
            newMap[phoneType] = { ...current, [PhoneKeys.ORDER]: index };
        });
        
        updateSetting(SETTING.PHONE_PREFERENCE, newMap);
        setDraggedIndex(null);
    }, [draggedIndex, sortedPhoneTypes, phonePreferenceMap, updateSetting]);

    const handleDragEnd = useCallback(() => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [])

   

    return (
        <div className="flex-1">
            <div className="space-y-1">
                {sortedPhoneTypes.map((phoneType, index) => {
                    const isActive = getValueForPhonePreference(phoneType, PhoneKeys.ACTIVE);
                    const doubleTaps = getValueForPhonePreference(phoneType, PhoneKeys.DOUBLE_TAPS);
                    const isDragging = draggedIndex === index;
                    const isDraggedOver = dragOverIndex === index;

                    return (
                        <div 
                            key={phoneType} 
                            className={`flex items-center justify-between py-1.5 px-2 rounded border transition-all duration-200 ${
                                isDragging 
                                    ? 'opacity-50 scale-95 bg-gray-100 border-gray-300' 
                                    : isDraggedOver 
                                        ? 'bg-blue-50 border-blue-200 border-2' 
                                        : 'bg-gray-50/50 border-gray-100'
                            }`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                        >
                            {/* Drag handle */}
                            <div className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-400 hover:text-gray-600 transition-colors">
                                <GripVertical size={14} />
                            </div>

                            {/* Toggle */}
                            <button
                                onClick={() => updatePhonePreference(phoneType, PhoneKeys.ACTIVE, !isActive)}
                                className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors duration-200 ${
                                    isActive ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                            >
                                <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform duration-200 ${
                                    isActive ? 'translate-x-3.5' : 'translate-x-0.5'
                                }`} />
                            </button>

                            {/* Phone type */}
                            <div className="flex items-center gap-1.5 flex-1 ml-2">
                                <Phone size={12} className="text-gray-600" />
                                <Typography variant="smallParagraph" color="#374151" fontWeight="500">
                                    {getPhoneLabel(phoneType, selectedPlatform)}
                                </Typography>
                            </div>

                            {/* Attempts */}
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    min={1}
                                    max={3}
                                    value={doubleTaps as number}
                                    onChange={(e) => {
                                        const newValue = parseInt(e.target.value);
                                        if (!isNaN(newValue) && newValue >= 1 && newValue <= 3) {
                                            updatePhonePreference(phoneType, PhoneKeys.DOUBLE_TAPS, newValue);
                                        }
                                    }}
                                    className="w-8 px-1 py-0.5 text-center border border-gray-300 rounded text-xs"
                                />
                                <Typography variant="smallParagraph" color="#6b7280" fontWeight="400" className="text-xs">
                                    taps
                                </Typography>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
} 